import { emailStatuses, emailStatusLabels } from "@purlink/shared/emails-api";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Inbox, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { PageHeader } from "~/components/shell/app-shell";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Alert, EmptyState, Skeleton } from "~/components/ui/feedback";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { emailListQuery, teammatesQuery, type EmailFilters } from "~/features/emails/api";
import { EmailDetailSheet } from "~/features/emails/email-detail-sheet";
import { EmailStatusBadge } from "~/features/emails/email-status";
import { FilterSelect } from "~/features/leads/filter-select";
import { errorMessage } from "~/lib/auth";
import { formatDateTime, formatRelative } from "~/lib/format";

const optional = z.string().optional().catch(undefined);

const searchSchema = z.object({
  q: optional,
  status: z.enum(emailStatuses).optional().catch(undefined),
  senderId: optional,
  email: optional,
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/emails")({
  validateSearch: searchSchema,
  component: EmailsPage,
});

function EmailsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [query, setQuery] = useState(search.q ?? "");
  const { email: openId, ...filters } = search;
  const emails = useInfiniteQuery(emailListQuery(filters as EmailFilters));
  const teammates = useQuery(teammatesQuery);
  const rows = emails.data?.pages.flatMap((page) => page.emails) ?? [];

  const setSearch = (patch: Partial<Search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== query.trim()) void setSearch({ q: query.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only react to typing
  }, [query]);

  const filtered = Boolean(search.q || search.status || search.senderId);
  const open = (id: string) => void setSearch({ email: id });
  const senderName = (row: (typeof rows)[number]) =>
    row.sentBy?.name ?? row.fromName.replace(/ via Purlink$/, "");

  return (
    <>
      <PageHeader
        title="Emails"
        description="Every email your team has sent to leads from Purlink, with delivery status."
      />

      <Card padding="sm" className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
            aria-hidden
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by subject, lead or address"
            aria-label="Search emails"
            className="h-9 pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            label="Status"
            allLabel="Any status"
            value={search.status}
            options={emailStatuses.map((value) => ({ value, label: emailStatusLabels[value] }))}
            onChange={(status) => setSearch({ status })}
          />
          <FilterSelect
            label="Sender"
            allLabel="Anyone"
            value={search.senderId}
            options={(teammates.data?.teammates ?? []).map((person) => ({
              value: person.id,
              label: person.id === teammates.data?.me ? `${person.name} (me)` : person.name,
            }))}
            onChange={(senderId) => setSearch({ senderId })}
          />
          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQuery("");
                void navigate({ search: {}, replace: true });
              }}
            >
              <X /> Clear
            </Button>
          )}
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        {emails.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : emails.isError ? (
          <div className="p-5">
            <Alert tone="error" title="Emails didn't load.">
              {errorMessage(emails.error)}
            </Alert>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Inbox />}
            title={filtered ? "No emails match" : "No emails sent yet"}
            description={
              filtered
                ? "Try different filters."
                : "Open a lead and choose Email to send your first message."
            }
          />
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Subject</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead className="hidden lg:table-cell">Sent by</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} className="cursor-pointer" onClick={() => open(row.id)}>
                      <TableCell className="max-w-80">
                        <button
                          type="button"
                          className="block max-w-full truncate text-left font-medium text-fg hover:text-primary focus-visible:underline focus-visible:outline-none"
                          onClick={(event) => {
                            event.stopPropagation();
                            open(row.id);
                          }}
                        >
                          {row.subject}
                        </button>
                        <span className="block truncate text-small text-fg-muted">
                          {row.excerpt}
                        </span>
                      </TableCell>
                      <TableCell>
                        {row.lead && !row.lead.deleted ? (
                          <Link
                            to="/leads/$leadId"
                            params={{ leadId: row.lead.id }}
                            className="text-fg-secondary hover:text-primary"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {row.lead.name}
                          </Link>
                        ) : (
                          <span className="text-fg-muted">
                            {row.lead?.name ?? row.to[0]}
                            {row.lead?.deleted && " (deleted)"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="hidden text-fg-secondary lg:table-cell">
                        {senderName(row)}
                      </TableCell>
                      <TableCell>
                        <EmailStatusBadge status={row.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-fg-secondary">
                        <time dateTime={row.createdAt} title={formatDateTime(row.createdAt)}>
                          {formatRelative(row.createdAt)}
                        </time>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <ul className="flex flex-col divide-y divide-border md:hidden">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => open(row.id)}
                    className="flex w-full flex-col gap-1 p-4 text-left hover:bg-surface-hover"
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="min-w-0 truncate font-medium text-fg">{row.subject}</span>
                      <EmailStatusBadge status={row.status} />
                    </span>
                    <span className="truncate text-small text-fg-secondary">
                      To {row.lead?.name ?? row.to[0]}
                    </span>
                    <span className="text-caption text-fg-muted">
                      {senderName(row)} · {formatRelative(row.createdAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {emails.hasNextPage && (
          <div className="flex justify-center border-t border-border p-3">
            <Button
              variant="ghost"
              size="sm"
              loading={emails.isFetchingNextPage}
              onClick={() => void emails.fetchNextPage()}
            >
              Show more
            </Button>
          </div>
        )}
      </Card>

      <EmailDetailSheet
        emailId={openId ?? null}
        onOpenChange={(next) => !next && void setSearch({ email: undefined })}
      />
    </>
  );
}
